import { useState, useEffect, useCallback } from 'react';
import {
  listSubtitles,
  createSubtitle,
  updateSubtitle,
  deleteSubtitle,
  generateSubtitles,
  cuesToSrt,
  listAudioTracks,
  AUDIO_KIND_META,
  SUBTITLE_FORMATS,
  SUBTITLE_ANIMATIONS,
  DEFAULT_SUBTITLE_STYLE,
  type ContentSubtitle,
  type SubtitleCue,
  type SubtitleStyle,
  type SubtitleFormat,
  type ContentScene,
  type ContentAudioTrack,
} from '@/lib/contentStudio';

interface Props {
  projectId: number;
  scenes: ContentScene[];
  language: string;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  return `${pad(m)}:${sec.padStart(4, '0')}`;
}

function stylePosition(style: SubtitleStyle | null): string {
  const p = style?.position || 'bottom';
  if (p === 'top') return 'items-start';
  if (p === 'center') return 'items-center';
  return 'items-end';
}

function animationClass(a?: SubtitleStyle['animation']): string {
  if (a === 'fade') return 'animate-[fadeIn_0.4s_ease]';
  if (a === 'pop') return 'scale-105 transition-transform';
  if (a === 'slide') return 'translate-y-0 transition-transform';
  return '';
}

/**
 * Sous-titres automatiques du projet : génération timecodée à partir de la
 * narration (synchronisée sur les pistes audio), édition des cues, styles
 * (format, taille, position, couleurs, animation) et export SRT.
 */
export default function SubtitlesPanel({ projectId, scenes, language }: Props) {
  const [tracks, setTracks] = useState<ContentSubtitle[]>([]);
  const [audioTracks, setAudioTracks] = useState<ContentAudioTrack[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [previewCue, setPreviewCue] = useState<SubtitleCue | null>(null);

  const [format, setFormat] = useState<SubtitleFormat>('standard');
  const [sourceSceneId, setSourceSceneId] = useState<string>('');

  const active = tracks.find((t) => t.id === activeId) || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subs, audio] = await Promise.all([listSubtitles(projectId), listAudioTracks(projectId)]);
      setTracks(subs);
      setAudioTracks(audio);
      setActiveId((prev) => (prev && subs.some((s) => s.id === prev) ? prev : (subs[0]?.id ?? null)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const voiceovers = audioTracks.filter((t) => t.kind === 'voiceover');

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const source = sourceSceneId
        ? scenes.filter((s) => String(s.id) === sourceSceneId)
        : scenes;
      const payloadScenes = source
        .filter((s) => (s.voiceover || s.script || '').trim())
        .map((s) => ({
          scene_id: s.id,
          number: s.number,
          duration: s.duration || 5,
          voiceover: (s.voiceover || s.script || '').trim(),
        }));
      if (payloadScenes.length === 0) {
        setError("Aucune narration à sous-titrer : renseignez la voix off des scènes.");
        setGenerating(false);
        return;
      }
      const cues = await generateSubtitles({ language, scenes: payloadScenes });
      if (cues.length === 0) {
        setError("Aucun sous-titre n'a pu être généré.");
        setGenerating(false);
        return;
      }
      const created = await createSubtitle(projectId, {
        format,
        style: DEFAULT_SUBTITLE_STYLE,
        cues,
        content: cues.map((c) => c.text).join('\n'),
        scene_id: sourceSceneId ? Number(sourceSceneId) : null,
        audio_track_id: voiceovers[0]?.id ?? null,
        status: 'ready',
      });
      await load();
      if (created) setActiveId(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de génération');
    } finally {
      setGenerating(false);
    }
  };

  const patchActive = (patch: Partial<ContentSubtitle>) => {
    if (!active) return;
    setTracks((prev) => prev.map((t) => (t.id === active.id ? { ...t, ...patch } : t)));
  };

  const saveActive = async () => {
    if (!active || saving) return;
    setSaving(true);
    setError(null);
    try {
      await updateSubtitle(active.id, {
        format: active.format,
        style: active.style,
        cues: active.cues,
        content: active.cues.map((c) => c.text).join('\n'),
        scene_id: active.scene_id,
        audio_track_id: active.audio_track_id,
        status: 'ready',
      });
      setFlash(true);
      setTimeout(() => setFlash(false), 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur d\u2019enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const editCue = (index: number, patch: Partial<SubtitleCue>) => {
    if (!active) return;
    const cues = active.cues.map((c, i) => (i === index ? { ...c, ...patch } : c));
    patchActive({ cues });
  };

  const addCue = () => {
    if (!active) return;
    const last = active.cues[active.cues.length - 1];
    const start = last ? last.end : 0;
    const next: SubtitleCue = { scene_id: last?.scene_id ?? null, scene_number: last?.scene_number ?? null, start, end: start + 2, text: 'Nouveau sous-titre' };
    patchActive({ cues: [...active.cues, next] });
  };

  const removeCue = (index: number) => {
    if (!active) return;
    patchActive({ cues: active.cues.filter((_, i) => i !== index) });
  };

  const setStyle = (patch: Partial<SubtitleStyle>) => {
    if (!active) return;
    patchActive({ style: { ...DEFAULT_SUBTITLE_STYLE, ...(active.style || {}), ...patch } });
  };

  const handleExportSrt = () => {
    if (!active) return;
    const srt = cuesToSrt(active.cues);
    const blob = new Blob([srt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sous-titres-projet-${projectId}.srt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sceneLabel = (id: number | null): string => {
    if (!id) return 'Projet entier';
    const s = scenes.find((x) => x.id === id);
    return s ? `Scène ${pad(s.number)}` : 'Scène';
  };

  const style = active?.style || DEFAULT_SUBTITLE_STYLE;

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-subtract-line text-foreground-500"></i>Sous-titres
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Générez des sous-titres timecodés depuis la narration, éditez chaque réplique, réglez le style et exportez
            en SRT. Ils se calent sur les pistes audio de la voix off.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Bloc génération */}
      <div className="mb-5 bg-background-50 border border-background-200/70 rounded-xl p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Format</label>
            <div className="relative">
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value as SubtitleFormat)}
                className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {SUBTITLE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Source</label>
            <div className="relative">
              <select
                value={sourceSceneId}
                onChange={(e) => setSourceSceneId(e.target.value)}
                className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                <option value="">Toutes les scènes</option>
                {scenes.map((s) => <option key={s.id} value={s.id}>Scène {pad(s.number)}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div className="sm:col-span-2 flex items-end">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || scenes.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {generating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-2-line"></i>}
              {generating ? 'Génération…' : 'Générer les sous-titres'}
            </button>
          </div>
        </div>

        {/* Synchro audio */}
        {voiceovers.length > 0 && (
          <p className="mt-3 text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-link text-accent-500"></i>
            Synchronisation sur la voix off du projet :
            {voiceovers.map((v) => <span key={v.id} className="font-medium text-foreground-700">{v.name || AUDIO_KIND_META.voiceover.label}</span>)}
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : tracks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-subtract-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucun sous-titre</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">Générez une première piste de sous-titres à partir de la narration.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* Éditeur de cues */}
          <div className="xl:col-span-2">
            {/* Barre de pistes */}
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              {tracks.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveId(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer border ${
                    t.id === activeId ? 'bg-primary-100 border-primary-300 text-primary-800' : 'bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-film-line"></i>{SUBTITLE_FORMATS.find((f) => f.value === t.format)?.label || t.format}
                  <span className="text-foreground-400">· {t.cues.length}</span>
                </button>
              ))}
            </div>

            {active && (
              <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-background-200/70">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-bold text-foreground-700">{active.cues.length} sous-titres</span>
                    <span className="text-[11px] text-foreground-400 inline-flex items-center gap-1">
                      <i className="ri-movie-line"></i>{sceneLabel(active.scene_id)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {flash && <span className="text-xs text-accent-600 flex items-center gap-1"><i className="ri-check-line"></i>Enregistré</span>}
                    <button type="button" onClick={handleExportSrt} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                      <i className="ri-download-2-line"></i>SRT
                    </button>
                    <button type="button" onClick={saveActive} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer">
                      {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}Enregistrer
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-background-200/70 max-h-[420px] overflow-y-auto">
                  {active.cues.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 px-3 py-2.5 hover:bg-background-100/60">
                      <span className="mt-2 text-[10px] font-bold text-foreground-400 w-5 text-right flex-shrink-0">{i + 1}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                        <input
                          type="number"
                          step="0.1"
                          value={c.start}
                          onChange={(e) => editCue(i, { start: Number(e.target.value) })}
                          className="w-16 px-1.5 py-1 bg-background-50 border border-background-200/70 rounded text-[11px] text-foreground-800 focus:outline-none focus:border-accent-300"
                          title="Début (s)"
                        />
                        <span className="text-foreground-300 text-[10px]">→</span>
                        <input
                          type="number"
                          step="0.1"
                          value={c.end}
                          onChange={(e) => editCue(i, { end: Number(e.target.value) })}
                          className="w-16 px-1.5 py-1 bg-background-50 border border-background-200/70 rounded text-[11px] text-foreground-800 focus:outline-none focus:border-accent-300"
                          title="Fin (s)"
                        />
                      </div>
                      <textarea
                        value={c.text}
                        onChange={(e) => editCue(i, { text: e.target.value })}
                        onFocus={() => setPreviewCue(c)}
                        rows={1}
                        className="flex-1 min-w-0 px-2 py-1 bg-background-50 border border-background-200/70 rounded text-xs text-foreground-800 focus:outline-none focus:border-accent-300 resize-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeCue(i)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
                        title="Supprimer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="px-3 py-2 border-t border-background-200/70">
                  <button type="button" onClick={addCue} className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    <i className="ri-add-line"></i>Ajouter un sous-titre
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Styles + aperçu */}
          {active && (
            <div className="space-y-4">
              {/* Aperçu */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-3">
                <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                  <i className="ri-eye-line text-foreground-400"></i>Aperçu
                </p>
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-foreground-800 to-foreground-950 flex justify-center p-3">
                  <div className={`w-full h-full flex justify-center ${stylePosition(style)}`}>
                    <span
                      className={`inline-block max-w-[90%] text-center rounded-md px-2 py-1 leading-tight ${animationClass(style.animation)}`}
                      style={{
                        fontSize: `${Math.max(10, Math.round((style.fontSize || 22) * 0.55))}px`,
                        color: style.color,
                        backgroundColor: style.background,
                        fontWeight: 700,
                      }}
                    >
                      {(previewCue?.text || active.cues[0]?.text || 'Votre sous-titre apparaîtra ici').slice(0, 90)}
                    </span>
                  </div>
                  <span className="absolute bottom-1.5 right-2 text-[9px] text-white/60">{fmtTime(previewCue?.start || 0)}</span>
                </div>
                <p className="text-[10px] text-foreground-400 mt-2">Cliquez une réplique pour la prévisualiser.</p>
              </div>

              {/* Réglages style */}
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                  <i className="ri-palette-line text-foreground-400"></i>Style des sous-titres
                </p>
                <div>
                  <label className="block text-[11px] font-medium text-foreground-500 mb-1">Format</label>
                  <div className="relative">
                    <select
                      value={active.format}
                      onChange={(e) => patchActive({ format: e.target.value })}
                      className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
                    >
                      {SUBTITLE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-foreground-500 mb-1">Taille : {style.fontSize || 22}px</label>
                  <input type="range" min={14} max={48} value={style.fontSize || 22} onChange={(e) => setStyle({ fontSize: Number(e.target.value) })} className="w-full accent-[oklch(var(--primary-500))] cursor-pointer" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-foreground-500 mb-1">Position</label>
                  <div className="flex items-center gap-1 bg-background-100 rounded-md p-1">
                    {(['top', 'center', 'bottom'] as const).map((p) => (
                      <button key={p} type="button" onClick={() => setStyle({ position: p })} className={`flex-1 px-2 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${style.position === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                        {p === 'top' ? 'Haut' : p === 'center' ? 'Milieu' : 'Bas'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-500 mb-1">Couleur</label>
                    <input type="color" value={style.color || '#ffffff'} onChange={(e) => setStyle({ color: e.target.value })} className="w-full h-9 rounded-md border border-background-200/70 cursor-pointer bg-background-50" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-500 mb-1">Fond</label>
                    <input type="color" value={(style.background || '#000000').slice(0, 7)} onChange={(e) => setStyle({ background: `${e.target.value}99` })} className="w-full h-9 rounded-md border border-background-200/70 cursor-pointer bg-background-50" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-foreground-500 mb-1">Animation</label>
                  <div className="flex items-center gap-1 bg-background-100 rounded-md p-1 flex-wrap">
                    {SUBTITLE_ANIMATIONS.map((a) => (
                      <button key={a.value} type="button" onClick={() => setStyle({ animation: a.value })} className={`flex-1 px-2 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${style.animation === a.value ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => { if (active) { await deleteSubtitle(active.id); await load(); } }}
                  className="flex items-center justify-center gap-1.5 w-full py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-500 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>Supprimer cette piste
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}